// src/utils/lossRunHandoffPdf.js
//
// Render the Insurance History Handoff from a buildHandoffModel() model with
// pdf-lib, reusing the proposal toolkit: createProposalContext for fonts,
// colors, logo and paging; measureTable/drawTable for the tables (header repeat,
// no orphaned rows); drawBlocks for paginated prose. The proposal's own footer
// (ctx.finalize draws its disclaimer) is NOT used — this document draws its own
// "Prepared by" footer and page numbers, then saves.
//
// Download only (ACORD blob pattern). Nothing here writes to the vault.

import { createProposalContext, MARGIN_LEFT, MARGIN_RIGHT, PAGE_WIDTH, CONTENT_WIDTH, FOOTER_BASELINE, TYPE, BODY_LEADING } from './proposalLayout';
import { measureTable, drawTable, availableWidth } from './proposalTable';
import { drawBlocks } from './proposalProse';
import { drawTracked, applyTransform, measureTracked } from './pdfText';

const HEADING_GAP = 10;
const SECTION_GAP = 16;
export const CELL_PADDING = 5;   // pt under the last text line of every row and header
export const ID_FONT_SHRINK_MAX = 1.5;   // identifiers may shrink by at most this many pt
const ID_FONT_STEP = 0.5;
// kind: 'text' (ordinary words — may wrap at spaces, may be hyphenated when a
// word is wider than the column) | 'id' (identifiers such as policy numbers —
// never altered: kept on one line, shrunk a little if needed, and as a last
// resort wrapped only at an existing hyphen with nothing inserted) | 'range'
// ("<a> – <b>": wraps at most once, with the dash kept on the first line).
const col = (label, weight, kind = 'text') => ({ label, align: 'left', format: 'text', weight, kind });

const longestWord = (font, text, size, tracking) =>
  Math.max(0, ...String(text ?? '').split(/\s+/).filter(Boolean).map(w => measureTracked(font, w, size, tracking)));
const whole = (font, text, size) => measureTracked(font, String(text ?? ''), size, 0);

function waterFill(mins, weights, available) {
  const n = mins.length;
  const fixed = new Array(n).fill(false);
  let widths = new Array(n).fill(0);
  for (let iter = 0; iter < n + 1; iter++) {
    const fixedSum = mins.reduce((a, m, i) => (fixed[i] ? a + m : a), 0);
    const freeW = weights.reduce((a, w, i) => (fixed[i] ? a : a + w), 0);
    const remaining = Math.max(0, available - fixedSum);
    widths = widths.map((_, i) => (fixed[i] ? mins[i] : (weights[i] / freeW) * remaining));
    let changed = false;
    widths.forEach((w, i) => { if (!fixed[i] && w < mins[i]) { fixed[i] = true; changed = true; } });
    if (!changed) break;
  }
  return widths;
}

/**
 * Column widths such that no cell can overflow into its neighbour
 * (wrapTracked breaks only at spaces). Text columns need room for their
 * widest word; id columns need room for their widest WHOLE value so the
 * identifier stays on one line. Starts from the relative weights and grows
 * any column below its minimum, taking from the columns with slack.
 *
 * If the minimums exceed the page width: first shrink the id columns' font
 * (0.5 pt steps, at most ID_FONT_SHRINK_MAX); then, as a last resort, allow
 * id values to wrap at their own hyphens (nothing inserted) and text words
 * to be hyphenated.
 *
 * Returns { columns (with width_hint and fontSize), rows (cells prepared for
 * the wrapper), widths, idFontSize, lastResort }.
 */
export function fitColumns(ctx, columns, rows) {
  const n = columns.length;
  const available = availableWidth(n);
  const headFont = ctx.fonts[TYPE.tableHeaderCell.font], headSize = TYPE.tableHeaderCell.size, headTr = TYPE.tableHeaderCell.tracking;
  const bodyFont = ctx.fonts[TYPE.tableBodyCell.font], bodySize = TYPE.tableBodyCell.size;
  const isId = columns.map(c => c.kind === 'id');
  // 'range' = "<a> – <b>": the column is kept at least as wide as "<a> –" and
  // "<b>", so the wrapper (which breaks only at spaces) always lands the dash
  // with the first value — two lines at most, never a dash on its own line.
  const isRange = columns.map(c => c.kind === 'range');
  const rangeMin = (font, size, cell) => {
    const [a, b] = String(cell ?? '').split(' – ');
    return Math.max(whole(font, `${a ?? ''} –`, size), whole(font, b ?? '', size));
  };
  const weights = columns.map(c => (Number.isFinite(c.weight) && c.weight > 0 ? c.weight : 1));
  const headerMins = columns.map(c => longestWord(headFont, applyTransform(c.label, TYPE.tableHeaderCell.transform), headSize, headTr));

  const minsAt = (idSize, idWholeValue) => columns.map((c, ci) => {
    let m = headerMins[ci];
    for (const r of rows) {
      m = Math.max(m, isId[ci]
        ? (idWholeValue ? whole(bodyFont, r[ci], idSize) : longestWord(bodyFont, String(r[ci] ?? '').replace(/-/g, '- '), idSize, 0))
        : isRange[ci] ? rangeMin(bodyFont, bodySize, r[ci])
          : longestWord(bodyFont, r[ci], bodySize, 0));
    }
    return m + 1; // hairline of slack against rounding
  });
  const fits = mins => mins.reduce((a, b) => a + b, 0) <= available;

  // 1. Identifiers whole, at the body size, then shrinking.
  let idFontSize = bodySize;
  let mins = minsAt(idFontSize, true);
  while (!fits(mins) && idFontSize - ID_FONT_STEP >= bodySize - ID_FONT_SHRINK_MAX) {
    idFontSize = +(idFontSize - ID_FONT_STEP).toFixed(2);
    mins = minsAt(idFontSize, true);
  }
  let lastResort = false;
  if (!fits(mins)) {
    // 2. Last resort: identifiers may break at their own hyphens.
    lastResort = true;
    mins = minsAt(idFontSize, false);
  }
  const widths = fits(mins)
    ? waterFill(mins, weights, available)
    : weights.map(w => (w / weights.reduce((a, b) => a + b, 0)) * available); // text words will be hyphenated below

  const fittedRows = rows.map(r => r.map((cell, ci) => (isId[ci]
    ? (lastResort ? splitAtHyphens(bodyFont, idFontSize, cell, widths[ci]) : String(cell ?? ''))
    : breakLongTokens(bodyFont, bodySize, cell, widths[ci]))));

  return {
    columns: columns.map((c, i) => ({
      label: c.label, align: c.align, format: c.format, width_hint: widths[i],
      ...(isId[i] && idFontSize !== bodySize ? { fontSize: idFontSize } : {}),
    })),
    rows: fittedRows,
    widths,
    idFontSize,
    lastResort,
  };
}

// Identifier last resort: break ONLY at the value's own hyphens, inserting
// nothing. Pieces are greedy hyphen-delimited groups, so no two adjacent
// pieces fit together on one line and the wrapper cannot re-join them with a
// space. A value with no hyphen is returned as-is (and will overflow — never
// altered). Removing the breaks restores the stored value exactly.
export function splitAtHyphens(font, size, text, maxWidth) {
  const s = text === null || text === undefined ? '' : String(text);
  if (!s || measureTracked(font, s, size, 0) <= maxWidth || !s.includes('-')) return s;
  const parts = s.split('-');
  const groups = parts.map((g, i) => (i < parts.length - 1 ? `${g}-` : g)).filter(Boolean); // keep each hyphen with its group
  const pieces = [];
  let cur = '';
  for (const g of groups) {
    if (cur && measureTracked(font, cur + g, size, 0) > maxWidth) { pieces.push(cur); cur = g; }
    else cur += g;
  }
  if (cur) pieces.push(cur);
  return pieces.join(' ');
}

// "INTERNATIONAL" in a 50 pt column -> "INTERNA- TIONAL" (chunks joined by a
// space so the wrapper breaks there). Tokens that fit are returned untouched.
// Ordinary words only — never used on identifier columns.
export function breakLongTokens(font, size, text, maxWidth) {
  const s = text === null || text === undefined ? '' : String(text);
  if (!s) return s;
  const out = [];
  for (const token of s.split(/\s+/).filter(Boolean)) {
    if (measureTracked(font, token, size, 0) <= maxWidth) { out.push(token); continue; }
    let chunk = '';
    for (const ch of token) {
      const trial = chunk + ch;
      if (chunk && measureTracked(font, trial + '-', size, 0) > maxWidth) { out.push(chunk + '-'); chunk = ch; }
      else chunk = trial;
    }
    if (chunk) out.push(chunk);
  }
  return out.join(' ');
}

function heading(ctx, text) {
  const spec = TYPE.sectionHeading;
  ctx.ensureSpace(spec.size + HEADING_GAP + BODY_LEADING * 2);
  const page = ctx.pages[ctx.pages.length - 1];
  drawTracked(page, applyTransform(text, spec.transform), {
    x: MARGIN_LEFT, y: ctx.cursor - spec.size, font: ctx.fonts[spec.font], size: spec.size,
    color: ctx.colors.primary, tracking: spec.tracking, overflow: ctx.overflow(),
  });
  ctx.cursor -= spec.size + HEADING_GAP + 4;
}

function prose(ctx, blocks, opts) {
  drawBlocks(ctx, Array.isArray(blocks) ? blocks : [blocks], opts);
}

function table(ctx, title, columns, rows) {
  if (!rows.length) { prose(ctx, 'None on record.'); return; }
  const fitted = fitColumns(ctx, columns, rows);
  const t = { title, columns: fitted.columns, rows: fitted.rows, cellPadding: CELL_PADDING };
  const plan = measureTable(ctx, t);
  drawTable(ctx, t, plan);
  ctx.cursor -= 10;
}

function drawTitleBlock(ctx, header) {
  const page = ctx.pages[ctx.pages.length - 1];
  let y = ctx.cursor;
  if (ctx.logoImage) {
    const dims = ctx.logoImage.scale(1);
    const h = 44, w = (dims.width / dims.height) * h;
    page.drawImage(ctx.logoImage, { x: MARGIN_LEFT, y: y - h, width: w, height: h });
    y -= h + 14;
  }
  const t = TYPE.sectionCoverTitle;
  drawTracked(page, applyTransform('Insurance History Handoff', t.transform), { x: MARGIN_LEFT, y: y - t.size, font: ctx.fonts[t.font], size: t.size, color: ctx.colors.primary, tracking: t.tracking });
  y -= t.size + 10;
  drawTracked(page, header.insuredName, { x: MARGIN_LEFT, y: y - 14, font: ctx.fonts.bold, size: 14, color: ctx.colors.text });
  y -= 14 + 6;
  const lines = [];
  if (header.dba) lines.push(`DBA ${header.dba}`);
  lines.push(`Mailing address: ${header.mailingAddress}`);
  lines.push(`Prepared ${header.preparedDate} by ${header.preparedBy}`);
  for (const line of lines) {
    drawTracked(page, line, { x: MARGIN_LEFT, y: y - 10, font: ctx.fonts.regular, size: 10, color: ctx.colors.text });
    y -= 14;
  }
  y -= 6;
  page.drawLine({ start: { x: MARGIN_LEFT, y }, end: { x: MARGIN_LEFT + CONTENT_WIDTH, y }, thickness: 1, color: ctx.colors.primary });
  ctx.cursor = y - SECTION_GAP;
}

/**
 * generateHandoffPDF(model, { brandAssets, settings }) -> Uint8Array
 */
export async function generateHandoffPDF(model, { brandAssets, settings } = {}) {
  const ctx = await createProposalContext({ brandAssets, settings });
  ctx.newPage();

  // 1. Header
  drawTitleBlock(ctx, model.header);

  // 2. Policies by term, newest first
  heading(ctx, 'Policies by term');
  if (!model.terms.length) prose(ctx, 'No policies on record.');
  // Relative width weights: unbreakable tokens (policy numbers, dates) get
  // room so cells never overprint; prose columns wrap.
  for (const { term, policies } of model.terms) {
    table(ctx, term, [
      col('Line', 2.3), col('Carrier', 2.5), col('Policy number', 2.9, 'id'), col('Effective – Expiration', 2.5, 'range'),
      col('Status', 1.4), col('Agency of record', 2.2), col('Placed through', 1.8), col('Admitted', 1.5),
    ], policies.map(p => [p.line, p.carrier, p.policyNumber, `${p.effective} – ${p.expiration}`, p.status, p.agencyOfRecord, p.placedThrough, p.admittedStatus]));
  }
  ctx.cursor -= 6;
  prose(ctx, `${model.policyCount} polic${model.policyCount === 1 ? 'y' : 'ies'} on record. Quotes and submissions are not listed.`, { color: ctx.colors.grey, size: TYPE.legal.size });
  ctx.cursor -= SECTION_GAP;

  // 3. Who to ask
  heading(ctx, 'Who to ask for loss runs');
  if (!model.whoToAsk.length) prose(ctx, 'No contactable source is assigned to these policies.');
  for (const s of model.whoToAsk) {
    prose(ctx, [
      { heading: `${s.name} — ${s.kind}`, text: `Contact: ${s.contactName} · ${s.email} · ${s.phone}` },
      `Covers: ${s.lines.join(', ')} — ${s.terms.join(', ')}`,
      `Default method: ${s.defaultMethod} · Insured authorization: ${s.authorization}`,
    ], { gap: 4 });
    ctx.cursor -= 8;
  }
  ctx.cursor -= SECTION_GAP - 8;

  // 4. Loss runs on file
  heading(ctx, 'Loss runs on file');
  table(ctx, 'Received', [col('Source', 2.0), col('Method', 1.8), col('Terms covered', 3.6), col('Policies covered', 3.0), col('Received', 1.7), col('Valued as of', 1.7)],
    model.onFile.map(r => [r.source, r.method, r.termsCovered, r.coverage, r.dateReceived, r.valuedAsOf]));
  ctx.cursor -= 6;
  if (model.open.length) {
    prose(ctx, [{ heading: 'Open requests' }, ...model.open.map(r => `${r.source} · ${r.method} · ${r.termsCovered} · ${r.coverage} — ${r.requestedLine}`)], { gap: 3 });
    ctx.cursor -= 6;
  }
  prose(ctx, model.copiesLine);
  ctx.cursor -= SECTION_GAP;

  // 5. Authorization letter
  heading(ctx, 'Loss run authorization letter');
  const a = model.authorization;
  prose(ctx, a.onFile === 'yes'
    ? `On file. Signed ${a.signedDate}.`
    : a.onFile === 'no' ? 'Not on file.' : 'Unknown — not recorded.');

  // Footer on every page, then save (not ctx.finalize — that is the proposal footer).
  const total = ctx.pages.length;
  const f = TYPE.footer;
  ctx.pages.forEach((page, i) => {
    drawTracked(page, `Prepared by ${model.header.preparedBy} · ${model.header.preparedDate} · Confidential`, { x: MARGIN_LEFT, y: FOOTER_BASELINE, font: ctx.fonts[f.font], size: f.size, color: ctx.colors.grey });
    drawTracked(page, `Page ${i + 1} of ${total}`, { x: PAGE_WIDTH - MARGIN_RIGHT, y: FOOTER_BASELINE, font: ctx.fonts[f.font], size: f.size, color: ctx.colors.grey, align: 'right' });
  });
  return ctx.pdfDoc.save();
}

// Browser download — same shape as generateACORD25.
export function downloadPdfBytes(bytes, filename) {
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
